'use client';
import React from 'react';
import { useCmsClientContext } from '../../utils/cmsPageContext';
import { getClientTranslations } from '../../utils/translations/getClientTranslations';
import { TRANSLATIONS } from './i18n';
import { useProductFilterState } from './ProductFilterProvider';
import { optionKeyOf } from './productFilterTypes';

/** The chip that was removed, as the analytics plan reports it. */
export type ActiveFilterRemoval = {
  categoryId: string;
  categoryName?: string;
  value: string;
  label: string;
};

export type ActiveFilterChipsProps = {
  /** A chip removed its own value. Receives the group and option, for analytics. */
  onRemove?: (removal: ActiveFilterRemoval) => void;
};

/**
 * What is currently applied, above the results, each chip removing its own
 * value. On mobile the panel is a sheet the reader has dismissed, so without
 * this the only visible evidence that the list is narrowed is that it is short.
 *
 * Labels come from the resolved options rather than the URL: the URL carries
 * uids, and a chip reading `dbs` tells the reader less than `DBS` does.
 */
export const ActiveFilterChips: React.FC<ActiveFilterChipsProps> = ({
  onRemove,
}) => {
  const { categories, selection, toggleValue, clearAll, activeCount } =
    useProductFilterState();

  // Before the client context is read, not after: with nothing applied there is
  // nothing to render, and `useCmsClientContext` throws outside a provider — so
  // reading it first would make a component that renders nothing still require
  // one, which is what broke every bare render of the layout above it.
  if (activeCount === 0) return null;

  return (
    <Chips {...{ categories, selection, toggleValue, clearAll, onRemove }} />
  );
};

const Chips: React.FC<
  Pick<
    ReturnType<typeof useProductFilterState>,
    'categories' | 'selection' | 'toggleValue' | 'clearAll'
  > &
    ActiveFilterChipsProps
> = ({ categories, selection, toggleValue, clearAll, onRemove }) => {
  const { brand, lang } = useCmsClientContext();
  const t = getClientTranslations(TRANSLATIONS, { brand, lang });

  const chips = categories.flatMap((category) => {
    const categoryId = category.categoryId;
    if (!categoryId) return [];
    return (selection[categoryId] ?? []).map((value) => ({
      categoryId,
      categoryName: category.name,
      value,
      // A selected value always resolves, because the selection is narrowed to
      // the offered options when it is read.
      label:
        category.options.find((option) => optionKeyOf(option) === value)?.label ??
        value,
    }));
  });

  return (
    <div
      data-active-filter-chips=""
      className="flex flex-wrap items-center gap-2"
    >
      {chips.map((chip) => (
        <button
          key={`${chip.categoryId}:${chip.value}`}
          type="button"
          onClick={() => {
            toggleValue(chip.categoryId, chip.value);
            onRemove?.(chip);
          }}
          // The accessible name says what pressing it does; the visible text is
          // just the value, which on its own reads as a label rather than a
          // control.
          aria-label={`${t.sort_and_filter_remove_filter} ${chip.label}`}
          className="mhg-alpha-typography-body-2 flex items-center gap-1 rounded-radius/button border border-borders/default bg-fill/card px-3 py-1 text-sm text-text/primary"
        >
          {chip.label}
          <svg aria-hidden="true" viewBox="0 0 24 24" className="size-3">
            <path
              d="M6 6l12 12M18 6L6 18"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        </button>
      ))}

      {/* Only worth offering once more than one chip is showing: with a single
          chip it does the same thing as the chip itself. */}
      {chips.length > 1 && (
        <button
          type="button"
          onClick={clearAll}
          className="mhg-alpha-typography-button-2 text-text/link"
        >
          {t.sort_and_filter_clear_all}
        </button>
      )}
    </div>
  );
};
